import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { MeetingCorrectionScope } from '@wundo/shared/meetingPage.ts';
import { correctMeeting } from './meetingPage.ts';
import type { CalendarMeetingItem } from './meetings.ts';
import { m } from '@/paraglide/messages.js';

/* How long the confirmation — and the way back with it — stays on screen. */
const TOAST_FOR_MS = 5000;

export interface AddToWundo {
  /** Add this meeting. Nothing is asked; the toast confirms it and can undo it. */
  add: (meeting: CalendarMeetingItem) => void;
  /** Take the last one back, exactly as it was made. */
  undo: () => void;
  /** Close the toast, when another action on the page speaks next. */
  dismiss: () => void;
  /** What the toast says, or null while it has nothing to say. */
  message: string | null;
  /** False once the toast is only reporting a failure — there is nothing to undo. */
  undoable: boolean;
}

/**
 * Adding a meeting to Wundo from the agenda, in one click.
 *
 * No question is put to the user. A meeting is added for the whole series —
 * every 1:1 with that person, every edition of that recurring meeting — because
 * "just this once" is almost never what somebody means by pointing at a
 * repeating conversation, and the thread is the thing Wundo actually works
 * with. Events Wundo never picks up by itself are the exception: the server
 * refuses a standing choice about them, so those are added for the one
 * occurrence, silently, rather than asking a question with one possible answer.
 *
 * What makes that safe without a confirmation in front of it is the way back:
 * the toast carries it, and it undoes exactly the correction that was made.
 *
 * The toast says one thing — that it was added — whatever the reach was. A line
 * naming the series it just signed you up for is a sentence to read where a
 * confirmation was wanted, and it is the busiest moment on the page: somebody
 * adding four meetings in a sweep reads it four times.
 */
export function useAddToWundo(userId: string | undefined): AddToWundo {
  const queryClient = useQueryClient();
  const [added, setAdded] = useState<CalendarMeetingItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [undoable, setUndoable] = useState(false);

  /* One message at a time, and never one that outstays the action it reports. */
  useEffect(() => {
    if (message === null) return;
    const timer = setTimeout(() => setMessage(null), TOAST_FOR_MS);
    return () => clearTimeout(timer);
  }, [message]);

  const correct = useMutation({
    mutationFn: ({
      meeting,
      follow,
    }: {
      meeting: CalendarMeetingItem;
      follow: boolean | null;
    }) => correctMeeting(userId!, meeting.id, { follow, scope: scopeFor(meeting) }),
    onSuccess: async () => {
      /*
       * The week is read again rather than patched here: whether Wundo follows
       * a meeting is the server's answer, and a card that flipped itself would
       * be lying the moment the two disagree.
       */
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  const add = (meeting: CalendarMeetingItem) => {
    correct.mutate(
      { meeting, follow: true },
      {
        onSuccess: () => {
          setAdded(meeting);
          setMessage(m.agenda_added());
          setUndoable(true);
        },
        onError: () => {
          setAdded(null);
          setMessage(m.agenda_add_failed());
          setUndoable(false);
        },
      },
    );
  };

  const undo = () => {
    if (!added) return;
    const meeting = added;
    setMessage(null);
    setAdded(null);
    /*
     * Back to having said nothing, rather than to "no, not this one": the user
     * is undoing a click, not making the opposite choice, and a standing "never
     * follow this" is a heavier thing than what they just did.
     */
    correct.mutate(
      { meeting, follow: null },
      {
        onError: () => {
          setMessage(m.agenda_undo_failed());
          setUndoable(false);
        },
      },
    );
  };

  const dismiss = () => setMessage(null);

  return { add, undo, dismiss, message, undoable };
}

/**
 * How far a correction about Wundo's help reaches. Always the whole series,
 * except where the server would refuse one — an event nobody attends, one with
 * a crowd, one that runs all day is never picked up automatically, so only this
 * one occurrence can be turned on, and only that one can be turned off again.
 *
 * The meeting page asks the same question of the same rule: whichever surface a
 * conversation is added on or taken out of, the choice reaches exactly as far.
 */
export function wundoScope(neverAutomatic: boolean): MeetingCorrectionScope {
  return neverAutomatic ? 'occurrence' : 'always';
}

function scopeFor(meeting: CalendarMeetingItem): MeetingCorrectionScope {
  return wundoScope(meeting.never_automatic);
}
